// ============================================================
// SEARCHABLE SELECT
//
// Drop-in replacement for a native <select> when the options list is
// long enough that typing to filter is faster than scrolling.
//
// Usage:
//   <SearchableSelect
//     value={form.occupation_category}
//     options={OCCUPATION_CATEGORIES}
//     onChange={v => up('occupation_category', v)}
//     placeholder="Select category"
//   />
//
// Options can be:
//   - Array of strings:  ['MDP', 'PNC', ...]
//   - Array of objects:  [{ value: 'MDP', label: 'MDP' }, ...]
//
// A leading blank option is built in (cleared via the × button inside the
// input when a value is set).
//
// Styling is hand-tuned to match the app's dark theme (borders var(--border),
// background var(--bg), accent var(--link)). No external deps.
// ============================================================

import React, { useState, useRef, useEffect, useMemo } from 'react';

const PANEL_MAX_HEIGHT = 240;

export default function SearchableSelect({
  value,
  options = [],
  onChange,
  placeholder = 'Select…',
  disabled = false,
  style = {},
  // Optional: when provided, if the user's search query has no exact-match
  // in the existing options, show a "+ Add '<query>' as new" row at the
  // bottom of the dropdown. Clicking it calls `onAddNew(query)` and
  // awaits the returned value (the new option's value), then picks it.
  // Useful for lists that can be extended from the UI (e.g. ballot boxes
  // for admins). Pass `undefined` to disable.
  onAddNew,
  addNewLabel = 'Add',   // rendered as `+ {addNewLabel} "<query>"`
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [adding, setAdding] = useState(false);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  
  // Normalize options to {value, label} objects so internal logic is uniform.
  const normalized = useMemo(
    () => options.map(o =>
      typeof o === 'string' ? { value: o, label: o } : o
    ),
    [options]
  );
  
  // Filter options against the search query (case-insensitive substring).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(o => o.label.toLowerCase().includes(q));
  }, [normalized, query]);
  
  // Label for the currently-selected value (shown when closed).
  const selectedLabel = useMemo(() => {
    if (value == null || value === '') return '';
    const hit = normalized.find(o => o.value === value);
    return hit ? hit.label : String(value);
  }, [value, normalized]);
  
  // "Add new" row gating: only shown if onAddNew is provided AND the
  // trimmed query has content AND no existing option matches it
  // exactly (case-insensitive). Prevents users from creating dupes
  // that differ only in casing.
  const trimmedQuery = query.trim();
  const exactMatchExists = useMemo(() => {
    if (!trimmedQuery) return false;
    const lc = trimmedQuery.toLowerCase();
    return normalized.some(o => o.label.toLowerCase() === lc);
  }, [normalized, trimmedQuery]);
  const canAdd = Boolean(onAddNew) && trimmedQuery.length > 0 && !exactMatchExists;
  
  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  
  // Focus the search box when opened, reset highlight.
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightIdx(0);
      // Next tick so the input is actually in the DOM.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);
  
  // Keep highlight in-bounds when filter shrinks. Total row count is
  // filtered.length plus 1 for the "add new" row, if present.
  const totalRows = filtered.length + (canAdd ? 1 : 0);
  useEffect(() => {
    if (highlightIdx >= totalRows) {
      setHighlightIdx(Math.max(0, totalRows - 1));
    }
  }, [totalRows, highlightIdx]);
  
  // Scroll the highlighted row into view as the user arrow-keys.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-idx="${highlightIdx}"]`);
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);
  
  const pick = (val) => {
    onChange?.(val);
    setOpen(false);
  };
  
  const clear = (e) => {
    e.stopPropagation();
    onChange?.('');
  };
  
  // Call onAddNew(query), await the returned new-value, and pick it.
  // Shows an "Adding…" state during the in-flight promise so the user
  // sees something happening (DB inserts can take a moment).
  const addAndPick = async () => {
    if (!onAddNew || !trimmedQuery || adding) return;
    setAdding(true);
    try {
      const newVal = await onAddNew(trimmedQuery);
      if (newVal) pick(newVal);
    } catch (err) {
      console.error('[SearchableSelect] onAddNew failed:', err);
    } finally {
      setAdding(false);
    }
  };
  
  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, Math.max(0, totalRows - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // If highlight is on the "add new" row, trigger add.
      if (canAdd && highlightIdx === filtered.length) {
        addAndPick();
      } else {
        const hit = filtered[highlightIdx];
        if (hit) pick(hit.value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };
  
  // Trigger ("closed") looks like a native select. Typing is disabled; the
  // trigger opens a panel that contains the real search input.
  const trigger = (
    <div
      onClick={() => !disabled && setOpen(o => !o)}
      style={{
        padding: '7px 10px',
        borderRadius: 7,
        border: '1px solid var(--border)',
        background: 'var(--bg)',
        color: selectedLabel ? 'var(--text)' : 'var(--text-3)',
        fontSize: 11,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        boxSizing: 'border-box',
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      <span style={{
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
      }}>
        {selectedLabel || placeholder}
      </span>
      {selectedLabel && !disabled && (
        <button
          type="button"
          onClick={clear}
          style={{
            background: 'transparent', border: 'none', color: 'var(--text-3)',
            cursor: 'pointer', fontSize: 13, padding: '0 4px', lineHeight: 1
          }}
          title="Clear">
          ×
        </button>
      )}
      <span style={{ color: 'var(--text-4)', fontSize: 9 }}>▾</span>
    </div>
  );
  
  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {trigger}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0, right: 0,
            zIndex: 50,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 7,
            boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}>
          <div style={{ padding: 6, borderBottom: '1px solid var(--border)' }}>
            <input
              ref={searchRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setHighlightIdx(0); }}
              onKeyDown={onKey}
              placeholder="Type to search…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '6px 8px', borderRadius: 5,
                border: '1px solid var(--border)', background: 'var(--bg)',
                color: 'var(--text)', fontSize: 11, outline: 'none',
              }}
            />
          </div>
          <div
            ref={listRef}
            style={{ maxHeight: PANEL_MAX_HEIGHT, overflowY: 'auto' }}>
            {filtered.length === 0 && !canAdd && (
              <div style={{ padding: '10px 12px', color: 'var(--text-3)', fontSize: 10, textAlign: 'center' }}>
                No matches
              </div>
            )}
            {filtered.map((o, i) => {
              const isSel = o.value === value;
              const isHi = i === highlightIdx;
              return (
                <div
                  key={o.value}
                  data-idx={i}
                  onMouseEnter={() => setHighlightIdx(i)}
                  onClick={() => pick(o.value)}
                  style={{
                    padding: '6px 10px',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: isSel ? 'var(--link)' : 'var(--text)',
                    background: isHi ? 'var(--border)' : 'transparent',
                    fontWeight: isSel ? 600 : 400,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  {isSel && <span style={{ color: 'var(--link)', fontSize: 10 }}>✓</span>}
                  {!isSel && <span style={{ width: 10 }} />}
                  <span>{o.label}</span>
                </div>
              );
            })}
            {canAdd && (() => {
              const i = filtered.length;
              const isHi = i === highlightIdx;
              return (
                <div
                  data-idx={i}
                  onMouseEnter={() => setHighlightIdx(i)}
                  onClick={addAndPick}
                  style={{
                    padding: '8px 10px',
                    cursor: adding ? 'wait' : 'pointer',
                    fontSize: 11,
                    color: '#6ee7b7',
                    background: isHi ? '#065f46' : '#064e3b22',
                    borderTop: filtered.length > 0 ? '1px solid var(--border)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontStyle: adding ? 'italic' : 'normal',
                  }}>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>{adding ? '…' : '+'}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {adding ? `Adding "${trimmedQuery}"…` : `${addNewLabel} "${trimmedQuery}"`}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
